import { memo, useCallback, useMemo, useState } from 'react'
import { ModalShell } from '../common/ModalShell.js'
import { Spinner } from '../common/Spinner.js'
import { broadcastDayLabel } from '../../lib/broadcast-day.js'
import type { DuplicateInsertion, InsertionRecipe } from './insertion.js'

interface DuplicateInsertionDialogProps {
  recipe: InsertionRecipe
  duplicates: DuplicateInsertion[]
  /** Rend les copies retenues, dans l'ordre où elles passent à l'antenne. */
  onApply: (selected: DuplicateInsertion[]) => Promise<void>
  onClose: () => void
}

function fmtBoundary(boundaryMs: number): string {
  const date = new Date(boundaryMs)
  const day = broadcastDayLabel(date, { weekday: 'short', day: 'numeric', month: 'short' })
  const time = date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  return `${day} à ${time}`
}

/**
 * La même séquence repasse ailleurs dans la semaine : propose d'y répercuter
 * l'insertion qui vient d'être faite.
 *
 * Rien ne relie une rediffusion à sa source en base — la reconnaissance est
 * faite sur le voisinage, donc faillible. D'où la question plutôt que
 * l'automatisme, et une case par copie : c'est l'opérateur qui tranche.
 */
export const DuplicateInsertionDialog = memo(function DuplicateInsertionDialog({
  recipe, duplicates, onApply, onClose,
}: DuplicateInsertionDialogProps) {
  const fitting = useMemo(() => duplicates.filter(entry => entry.fits), [duplicates])
  const [selected, setSelected] = useState<Set<number>>(
    () => new Set(fitting.map(entry => entry.boundaryMs)),
  )
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const toggle = useCallback((boundaryMs: number) => {
    setSelected(current => {
      const next = new Set(current)
      if (next.has(boundaryMs)) next.delete(boundaryMs)
      else next.add(boundaryMs)
      return next
    })
  }, [])

  const handleApply = useCallback(async () => {
    const chosen = duplicates.filter(entry => selected.has(entry.boundaryMs) && entry.fits)
    if (chosen.length === 0) { onClose(); return }
    setBusy(true)
    setError('')
    try {
      await onApply(chosen)
      onClose()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Répercussion impossible')
    } finally {
      setBusy(false)
    }
  }, [duplicates, onApply, onClose, selected])

  return (
    <ModalShell title="Répercuter sur les rediffusions ?" onClose={onClose} closeDisabled={busy} size="medium">
      <div className="duplicate-insertion">
        <p className="duplicate-insertion__intro">
          La même séquence repasse {duplicates.length > 1 ? `${duplicates.length} fois` : 'une fois'} dans
          la semaine. Faut-il y insérer également {recipe.label} ?
        </p>

        <ul className="duplicate-insertion__list">
          {duplicates.map(entry => (
            <li key={entry.boundaryMs} className={`duplicate-insertion__row${entry.fits ? '' : ' is-blocked'}`}>
              <label>
                <input
                  type="checkbox"
                  checked={entry.fits && selected.has(entry.boundaryMs)}
                  disabled={!entry.fits || busy}
                  onChange={() => toggle(entry.boundaryMs)}
                />
                <span className="duplicate-insertion__when">{fmtBoundary(entry.boundaryMs)}</span>
                <span className="duplicate-insertion__detail">
                  {entry.fits
                    ? entry.plan.shift.length > 0
                      ? `infoservice raccourci · ${entry.plan.shift.length} élément(s) avancé(s)`
                      : 'infoservice raccourci'
                    : 'infoservice trop court à cet endroit'}
                </span>
              </label>
            </li>
          ))}
        </ul>

        {error && <div className="duplicate-insertion__error">{error}</div>}

        <footer className="duplicate-insertion__actions">
          <button type="button" className="tp-button" onClick={onClose} disabled={busy}>
            Ne rien répercuter
          </button>
          <button
            type="button"
            className="tp-button tp-button--primary"
            onClick={handleApply}
            disabled={busy || fitting.length === 0}
          >
            {busy ? <Spinner /> : `Répercuter (${selected.size})`}
          </button>
        </footer>
      </div>
    </ModalShell>
  )
})

export default DuplicateInsertionDialog
